import { useEffect } from 'react';
import { TrackingClientType } from '@/constants/business/tracking';
import { initPickerMessageBridge } from './bridge';
import {
  initVisualTrackingReporter,
  loadVisualTrackingRuntimeConfigs,
} from './runtime';
import { VISUAL_TRACKING_CONFIG_CHANGED_EVENT } from './configChange';
import { logger } from './logger';

export interface UseVisualTrackingLayoutInitOptions {
  isVisualTrackingPreview: boolean;
  orgId: string;
  accountId?: number | string;
  clientType: TrackingClientType;
}

export function useVisualTrackingLayoutInit(
  options: UseVisualTrackingLayoutInitOptions
): void {
  const { isVisualTrackingPreview, orgId, accountId, clientType } = options;

  useEffect(() => {
    if (!isVisualTrackingPreview) {
      return undefined;
    }

    const controller = initPickerMessageBridge();

    return () => {
      controller.destroy();
    };
  }, [isVisualTrackingPreview]);

  useEffect(() => {
    if (isVisualTrackingPreview || !orgId || !accountId) {
      return undefined;
    }

    let canceled = false;
    let setupVersion = 0;
    let destroyReporter: (() => void) | undefined;

    const setupReporter = () => {
      setupVersion += 1;
      const currentVersion = setupVersion;
      destroyReporter?.();
      destroyReporter = undefined;
      loadVisualTrackingRuntimeConfigs(clientType)
        .then((configs) => {
          if (canceled || currentVersion !== setupVersion || !configs.length) {
            return;
          }

          destroyReporter = initVisualTrackingReporter({
            configs,
            orgId,
            accountId,
            clientType,
          });
        })
        .catch((error) => {
          logger.warn('load visual tracking runtime config failed', error);
        });
    };

    setupReporter();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setupReporter();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener(
      VISUAL_TRACKING_CONFIG_CHANGED_EVENT,
      setupReporter
    );

    return () => {
      canceled = true;
      setupVersion += 1;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener(
        VISUAL_TRACKING_CONFIG_CHANGED_EVENT,
        setupReporter
      );
      destroyReporter?.();
    };
  }, [isVisualTrackingPreview, orgId, accountId, clientType]);
}
